import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { SKIP_LOADING_SPINNER } from '@ebw-monorepo/loading';
import { TranslateService } from '@ngx-translate/core';
import { Feature, MapBrowserEvent, Map as OLMap } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Extent, getTopLeft, getWidth } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import Point from 'ol/geom/Point';
import { Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { bbox } from 'ol/loadingstrategy';
import { get as getProjection, transform } from 'ol/proj';
import { ImageWMS, OSM, Source, TileWMS, WMTS, XYZ } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { Circle, Fill, Icon, Stroke, Style, Text } from 'ol/style';
import { StyleLike } from 'ol/style/Style';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { BehaviorSubject, Subject } from 'rxjs';
import { ClickOwnerLevel, ClickOwnerRegistration } from '../interfaces/click-owner.interface';
import { filter, take } from 'rxjs/operators';
import { ActiveLayers, LayerConfigurator } from '../components/sidebar/ebw-layers-configurator/layer-configurator.model';
import { IProjection } from '../interfaces/mapSettings.interface';
import { IMapConfig } from '../models/config-mappa.model';
import { LayerVector, LayerWMS, LayerWMTS, LayerWorld, LayerXYZ } from '../models/layer.model';
import { LayerStylesService } from './layer-styles.service';
import { SnackbarService } from './snackbar.service';
import Layer from 'ol/layer/Layer';

/**
 * Layer position enumerator. BASE for world layers and OVERLAY for layers that goes on top of the base.
 */
export enum LayerPosition {
  BASE = 'BASE',
  OVERLAY = 'OVERLAY'
}

/**
 * Standard names of Google Maps basemaps.
 */
export enum GoogleBasemapType {
  ROADMAP = 'roadmap',
  HYBRID = 'hybrid',
  SATELLITE = 'satellite',
  TERRAIN = 'terrain'
}

@Injectable({
  providedIn: 'root'
})
export class MapService {
  /************************
      SERVICE CONSTANTS
  ************************/

  /**
   * Key for the layer id property added to the Layer. This is also the unique identifyer of the layer along the application.
   */
  static ID_LAYER_KEY = 'id';

  /**
   * Key for the layer label property added to the Layer.
   */
  static LABEL_LAYER_KEY = 'label';

  /**
   * Key for the layer type property added to the Layer.
   */
  static TYPE_LAYER_KEY = 'type';

  /**
   * Key for the position property added to the Layer.
   */
  static POSITION_LAYER_KEY = 'position';

  /**
   * Key for the order property added to the Layer for baseworld list.
   */
  static ORDER_WORLD_LAYER_KEY = 'order';

  /**
   * Key for the Google Maps basemap type property added to the Layer
   */
  static TYPE_GOOGLE_LAYER_KEY = 'googleMapsTypology';

  /**
   * Key for the layer group property of the layer.
   */
  static GROUP_LAYER_KEY = 'layer_group';

  // /**
  //  * Key for the feature tag property added to the Layer.
  //  */
  // static FEATURE_TAG_KEY = 'featureTag';

  /**
   * Key for the source URL from which data from the layer are obtained.
   */
  static DATA_URL_LAYER_KEY = 'data_url';

  /**
   * Key for the source URL from which data from the layer are obtained.
   */
  static DATA_URL_DETAIL_LAYER_KEY = 'detail_url';

  /**
   * EPSG:4326 reference system indicator
   */
  static EPSG_4326_KEY = 'EPSG:4326';

  /**
   * EPSG:3857 reference system indicator
   */
  static EPSG_3857_KEY = 'EPSG:3857';

  // /**
  //  * clientID key
  //  */
  // static CLIENT_ID_TYPE = 'CLIENT_ID';

  /**
   * Key for layer visibility property.
   */
  static VISIBILITY_LAYER_KEY = 'visible';
  /**
   * Key for layer popup template property.
   */
  static POPUP_TEMPLATE_KEY = 'popupTemplate';

  /**
   * Key for the marker layer.
   */
  static MARKER_LAYER_KEY = 'markerLayer';

  /**
   * Key for the owner of the layer
   */
  static LAYER_OWNER_KEY = 'owner';

  /**
   * Key for the accessibility of the layer (public/private and in the future also group based)
   */
  static LAYER_ACCESSIBILITY = 'accessibility';

  // static SHAPE_LAYER_GROUP = 'shape';

  /**
   * Constant of the Open Layers map option. Used to control the map that is being displayed at any moment.
   */
  public SHOW_OPEN_LAYERS: number = 1;

  /**
   * Constant of the Google Maps map option. Used to control the map that is being displayed at any moment.
   */
  public SHOW_GOOGLE_MAPS: number = 2;

  /******************************
      SERVICE GLOBAL VARIABLES
  ******************************/
  //NB: All the variables are composed by a getter, setter, and change subject.
  //  in this way, all properties are accessible in real time and also changes to it can be subscribed
  //  through the change subject.
  //  VARIABLE CHANGE IS TRIGGERED AFTER THE VALUE IS SET TO THE PRIVATE LOCAL PROPERTY.

  /**
   * Variable to enable or disable the loading spinner on the MapComponent
   */
  loading: boolean = false;

  proxyUrl = '';

  layerOrder = ['THIRDPARTY', 'SHAPE', 'MAPSERVICE', 'TMPLAYER'];

  /**
   * Global object to store tokens for layers
   */
  private _tokens: {
    oauth: {
      [key: string]: string | Function;
    };
    Basic: {
      [key: string]: string;
    };
  } = { oauth: {}, Basic: {} };
  public getToken(authType: string, layer: string): string {
    return this._tokens[authType][layer];
  }
  public setToken(authType: string, layer: string, value: string | Function) {
    this._tokens[authType][layer] = value;
  }

  /**
   * Global map center coordinates in EPSG:4326 coordinate reference system.
   */
  private _mapCenter: number[] | null = null; //initially null - always in projection 4326
  public get mapCenter(): number[] | null {
    return this._mapCenter;
  }
  public set mapCenter(val: number[]) {
    this._mapCenter = val;
    this.mapCenterChanged.next(this._mapCenter);
  }
  mapCenterChanged: Subject<number[]> = new Subject();

  /**
   * Subject used to trigger the back button click event
   */
  backButtonEventSubject: Subject<boolean> = new Subject();

  /**
   * Global map zoom.
   */
  private _mapZoom: number | null = null; //initially null
  public get mapZoom(): number | null {
    return this._mapZoom;
  }
  public set mapZoom(val: number) {
    let newZoom = val;
    if (newZoom > this.maxZoom) newZoom = this.maxZoom;
    if (newZoom < this.minZoom) newZoom = this.minZoom;
    this._mapZoom = newZoom;
    this.mapZoomChanged.next(this._mapZoom);
  }
  mapZoomChanged: Subject<number> = new Subject();

  maxZoom: number = 100;
  minZoom: number = 0;

  /**
   * Global zoom overflow variable
   */
  private _previewZoomOverflow: number | null = null;
  public get previewZoomOverflow(): number | null {
    return this._previewZoomOverflow;
  }
  public set previewZoomOverflow(val: number) {
    this._previewZoomOverflow = val;
    this.previewZoomOverflowChanged.next(this._previewZoomOverflow);
  }
  previewZoomOverflowChanged: Subject<number> = new Subject();

  /**
   * Global map extent. Used in the preview.
   */
  private _mapExtent: Extent = null;
  public get mapExtent(): Extent {
    return this._mapExtent;
  }
  public set mapExtent(val: Extent) {
    this._mapExtent = val;
    this.mapExtentChanged.next(this._mapExtent);
  }
  mapExtentChanged: Subject<Extent> = new Subject();

  /**
   * Variable to store the map status in terms of drawing
   */
  public isMapDrawing: boolean = false;

  /**
   * The coordinates of the mouse. Are captured on the ebw-map component and used by the info-view component.
   */
  mouseCoordinates: number[] = [0, 0];

  /**
   * The globlal map resolution. Used in the info-view component for the scale of the map.
   */
  mapResolution: number | null = 0; //initially null

  /**
   * Active OVERLAY layers. It stores all the OVERLAY layers that are added to the map as a Map structure that is
   * accessible using the layer title as a unique identifier.
   *
   * It stores the OpenLayers BaseLayer object that contiains the Layer object and also the metadata inserted
   * as properties.
   */
  activeLayers: Map<string, BaseLayer> = new Map(); // The key is the title of the layer

  /**
   * Active BASE layers. It stores all the BASE layers that are added to the map as a Map structure that is
   * accessible using the layer title as a unique identifier.
   *
   * It stores the OpenLayers BaseLayer object that contiains the Layer object and also the metadata inserted
   * as properties.
   */
  worldLayers: Map<string, BaseLayer> = new Map();

  /**
   * Map of the layerGroups that exist on the OpenLayers map. It stores the layers that belong to the layer group.
   * It is accessible using the layerGroup title as a unique key.
   */
  layerGroups: Map<string, LayerGroup> = new Map();

  /**
   * The world layer (base map) that is active at any moment. This is a OpenLayers BaseLayer object that contains the
   * layer as well as metadata inserted as properties.
   * For GoogleMaps world layers it contains the Google Maps Type stored as property.
   */
  activeBaseLayer: BaseLayer = null;

  markerLayer: VectorLayer<any> = new VectorLayer({
    visible: true,
    source: new VectorSource()
  });

  tempMarkerLayer: VectorLayer<any> = new VectorLayer({
    visible: true,
    source: new VectorSource()
  });

  mapMarkers: Map<string, Feature> = new Map();
  tempMapMarkers: Map<string, Feature> = new Map();

  mapConfigurationLoaded$: BehaviorSubject<IMapConfig> = new BehaviorSubject(null);
  mapConfigurationReloadRequest$: Subject<boolean> = new Subject();
  allConfigurationReloadRequest$: Subject<boolean> = new Subject();
  timelines$: BehaviorSubject<any> = new BehaviorSubject(null);

  // behavior subject to notify when the layers have been loaded
  mapLayersLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // NEW

  /**
   * Temporary Layer
   */
  public get tempLayer(): VectorLayer<any> {
    return this._tempLayer;
  }

  public set tempLayer(value: VectorLayer<any>) {
    if (this._tempLayer) {
      //if the layer existed, clear it and remove it from the map
      this._tempLayer.getSource().clear();
      this._tempLayer.setMap(null);
    }

    this._tempLayer = value;
    this._tempLayer.setZIndex(this.layerOrder.indexOf('TMPLAYER') == -1 ? 0 : this.layerOrder.indexOf('TMPLAYER'));
  }

  _tempLayer: VectorLayer<any> = null;

  public get tempLayer2(): VectorLayer<any> {
    return this._tempLayer2;
  }

  public set tempLayer2(value: VectorLayer<any>) {
    if (this._tempLayer2) {
      //if the layer existed, clear it and remove it from the map
      this._tempLayer2.getSource().clear();
      this._tempLayer2.setMap(null);
    }

    this._tempLayer2 = value;
    this._tempLayer2.setZIndex(this.layerOrder.indexOf('TMPLAYER') == -1 ? 0 : this.layerOrder.indexOf('TMPLAYER'));
  }

  _tempLayer2: VectorLayer<any> = null;

  // NEW
  mapInputData;
  showBackButton = false;

  /**
   * Unlistener for the zoom-based Google basemap visibility check set up in changeWorldLayer.
   */
  private _worldLayerZoomListener: (() => void) | null = null;

  /**
   * Emits whether the Google Maps basemap div should be shown, based on the active world layer's maxZoom vs the current OL view zoom.
   */
  googleBasemapVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * Global Google Maps basemap type. More info https://developers.google.com/maps/documentation/javascript/maptypes
   */
  private _googleMapsBasemapType: string = GoogleBasemapType.ROADMAP;
  get googleMapsBasemapType(): string {
    return this._googleMapsBasemapType;
  }
  set googleMapsBasemapType(val: string) {
    this._googleMapsBasemapType = val;
    this.googleMapsBasemapTypeChanged.next(this._googleMapsBasemapType);
  }
  googleMapsBasemapTypeChanged: Subject<string> = new Subject();

  /**
   * Global variable that tells which map is being shown.
   * It can be SHOW_OPEN_LAYERS or SHOW_GOOGLE_MAPS.
   *
   * It shows the Open Layers map by default.
   */
  private _showingMap: number = this.SHOW_OPEN_LAYERS; //Showing open layers by default
  get showingMap(): number {
    return this._showingMap;
  }
  set showingMap(val: number) {
    this._showingMap = val;
    this.showingMapChanged.next(this._showingMap);
  }
  showingMapChanged: Subject<number> = new Subject();

  // /**
  //  * Global map configuration. It comes from the config-map.json file located in assets/config.
  //  * Contains information on map center, zoom, overlay layers, world layers, styles, etc.
  //  */
  // private _mapConfig: IMapConfig;
  // public get mapConfig(): IMapConfig {
  //   return this._mapConfig;
  // }
  // public set mapConfig(val: IMapConfig) {

  //   this._mapConfig = val;
  //   this.layerStylesService.mapStyles = this._mapConfig.styles;
  //   this.mapConfigurationReloaded$.next(val);
  // }

  registeredProjections: IProjection[];

  /**
   * The OpenLayers map object.
   */
  openLayersMap: OLMap = null;

  /**
   * Map to store the OpenLayers map interactions. Uses a title to identify different interactions faster.
   */
  mapInteractions: Map<string, Interaction> = new Map();

  /**
   * Variable to control if the OpenLayers map is rendered and ready to use.
   */
  private _readyOpenLayersMap: boolean = false;
  public get readyOpenLayersMap(): boolean {
    return this._readyOpenLayersMap;
  }
  public set readyOpenLayersMap(val: boolean) {
    this._readyOpenLayersMap = val;
    this.readyOpenLayersMapChanged.next(this._readyOpenLayersMap);
  }
  readyOpenLayersMapChanged: Subject<boolean> = new Subject();

  /**
   * Google Maps Map angular component object.
   */
  googleMapsMap: GoogleMap = null;

  /**
   * Variable to control if the Google Maps map is rendered and ready to use.
   */
  _readyGoogleMapsMap: boolean = false;
  public get readyGoogleMapsMap(): boolean {
    return this._readyGoogleMapsMap;
  }
  public set readyGoogleMapsMap(val: boolean) {
    this._readyGoogleMapsMap = val;
    this.readyOpenLayersMapChanged.next(this._readyGoogleMapsMap);
    this.readyGoogleMapsMapChanged.next(this._readyGoogleMapsMap);
  }
  readyGoogleMapsMapChanged: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public customMapClickEnable$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public mapClicked$: Subject<MapBrowserEvent<any>> = new Subject();

  /** Id del tool che detiene il click sulla mappa, null se libero (vedi acquireMapClick) */
  public mapClickOwner$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private _clickOwner: ClickOwnerRegistration = null;
  /** Tool passive che hanno ceduto il click (o non l'hanno ottenuto) e riprendono quando l'owner rilascia */
  private _waitingPassiveClickOwners: ClickOwnerRegistration[] = [];
  private static readonly CLICK_OWNER_RANK: Record<ClickOwnerLevel, number> = { passive: 0, weak: 1, strong: 2 };

  /**
   * Chiede il click sulla mappa per un tool. Va chiamato nel punto che APRE il tool, prima di attivare la sua interazione
   * (e prima di impostare customMapClickEnable$).
   * - libero: concesso.
   * - livello superiore a quello dell'owner: l'owner cede (onYield) e il click passa al nuovo tool.
   * - livello uguale o inferiore: rifiutato, con l'errore "c'è già uno strumento aperto" (tranne per i passive, che
   *   restano in attesa e partono con onResume quando l'owner rilascia).
   * @returns true se il click è stato concesso
   */
  acquireMapClick(registration: ClickOwnerRegistration): boolean {
    const owner = this._clickOwner;
    if (owner?.id === registration.id) {
      return true;
    }
    this._waitingPassiveClickOwners = this._waitingPassiveClickOwners.filter((waiting) => waiting.id !== registration.id);

    if (!owner) {
      this._setClickOwner(registration);
      return true;
    }

    if (MapService.CLICK_OWNER_RANK[registration.level] > MapService.CLICK_OWNER_RANK[owner.level]) {
      if (owner.level === 'passive') {
        this._waitingPassiveClickOwners.push(owner);
      }
      // il registro va aggiornato prima di onYield, così un release dell'owner che cede è un no-op
      this._setClickOwner(registration);
      owner.onYield?.();
      return true;
    }

    if (registration.level === 'passive') {
      this._waitingPassiveClickOwners.push(registration);
    } else {
      this.snackbarService.open(this._translateService.instant('map.click_owner.busy'), { duration: 3000 });
    }
    return false;
  }

  /**
   * Come acquireMapClick ma senza prendere il click: serve a chi deve decidere PRIMA di creare il tool (es. guard di una route),
   * quando l'acquisizione vera avviene più tardi (nel componente). Mostra l'errore se il click non sarebbe concesso.
   */
  canAcquireMapClick(registration: ClickOwnerRegistration): boolean {
    const owner = this._clickOwner;
    const allowed =
      !owner || owner.id === registration.id || MapService.CLICK_OWNER_RANK[registration.level] > MapService.CLICK_OWNER_RANK[owner.level];
    if (!allowed) {
      this.snackbarService.open(this._translateService.instant('map.click_owner.busy'), { duration: 3000 });
    }
    return allowed;
  }

  /**
   * Rilascia il click. Chi non è owner non fa nulla (a parte uscire dalla lista d'attesa dei passive).
   * Se c'è un passive in attesa riprende (onResume).
   */
  releaseMapClick(id: string): void {
    if (this._clickOwner?.id !== id) {
      this._waitingPassiveClickOwners = this._waitingPassiveClickOwners.filter((waiting) => waiting.id !== id);
      return;
    }
    const next = this._waitingPassiveClickOwners.pop();
    if (next) {
      this._setClickOwner(next);
      next.onResume?.();
    } else {
      this._setClickOwner(null);
    }
  }

  private _setClickOwner(owner: ClickOwnerRegistration): void {
    this._clickOwner = owner;
    this.mapClickOwner$.next(owner?.id ?? null);
  }
  constructor(
    private layerStylesService: LayerStylesService,
    private snackbarService: SnackbarService,
    private _translateService: TranslateService,
    private _httpClient: HttpClient
  ) {}

  /**
   * Shared authenticated tile/image loader for WMS/WMTS/XYZ sources, replacing raw XMLHttpRequest
   * so tile requests go through Angular's HttpClient pipeline (interceptors, centralized error handling).
   */
  private _authenticatedTileLoader(layer: LayerWMS | LayerWMTS | LayerXYZ, cacheBust = false) {
    return (tile: any, src: string) => {
      // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
      let url = src + (cacheBust ? '&rnd=' + Math.random() : '');
      let headers = new HttpHeaders();

      if (layer.auth.type === 'Basic') {
        headers = headers.set('Authorization', 'Basic ' + btoa(this._tokens[layer.auth.type][layer.id]));
      } else if (layer.auth.type === 'oauth') {
        const token =
          typeof this._tokens[layer.auth.type][layer.id] == 'function'
            ? (this._tokens[layer.auth.type][layer.id] as Function)()
            : (this._tokens[layer.auth.type][layer.id] as string);
        // Solo header: il servizio GSS dietro il proxy si rompe se riceve un parametro access_token
        // in query string. L'header Authorization era già l'unico meccanismo di auth realmente
        // attivo (nel vecchio codice XHR l'URL veniva mutato dopo `client.open()`, quindi
        // l'access_token in query string non è mai stato davvero inviato).
        headers = headers.set('Authorization', 'Bearer ' + token);
      }

      // SKIP_LOADING_SPINNER invece di un match sull'url nell'interceptor: l'url del proxy è
      // configurabile da DB (map.collection/layer config) e non garantito uguale in ogni ambiente/cliente
      const context = new HttpContext().set(SKIP_LOADING_SPINNER, true);
      this._httpClient.get(url, { headers, responseType: 'blob', context }).subscribe({
        next: (data) => {
          if (data && data.size > 0) {
            tile.getImage().src = URL.createObjectURL(data);
          } else {
            tile.setState(3);
          }
        },
        error: () => {
          console.log('Errore chiamata layer tile');
          tile.setState(3);
        }
      });
    };
  }

  /** Function to update the size of the OpenLayers map. */
  resizeOpenLayersMap() {
    if (this.readyOpenLayersMap) {
      this.openLayersMap.updateSize();
    }
  }

  /**
   * Centralized method for changing the world layer.
   * It automatically modifies all the variables related to world layer and triggers their change subjects.
   * @param title The title (ID) of the base layers as it is in the config file.
   */
  changeWorldLayer(layerId: string): void {
    const basemap = this.worldLayers.get(layerId);
    if (this.activeBaseLayer == basemap) return; //If it is the same basemap don't do anything.

    //If the world layer exists
    if (basemap) {
      const googleType = basemap.get(MapService.TYPE_GOOGLE_LAYER_KEY);

      if (googleType == '' || googleType == undefined) {
        //If it is not a google maps layer
        this.showingMap = this.SHOW_OPEN_LAYERS;
        this.googleMapsBasemapType = googleType;
      } else {
        //If it is a google maps layer
        this.showingMap = this.SHOW_GOOGLE_MAPS;
        this.googleMapsBasemapType = googleType;
      }

      this.activateWorldLayerZoomWatch(basemap);

      basemap.setVisible(true); //set new basemap visibility to true
      this.activeBaseLayer.setVisible(false); //set current basemap visibility to false
      this.activeBaseLayer = basemap; //set the new basemap as the current basemap
    }
    if (this.readyGoogleMapsMap) {
      //Finally set the google maps type id to the global googleMapsBasemapType
      this.googleMapsMap.googleMap.setMapTypeId(this.googleMapsBasemapType);
    }
  }

  /**
   * (Re)installs the OL view zoom listener that toggles Google Maps basemap div visibility
   * for world layers with a finite maxZoom (Google satellite/hybrid have no real imagery past
   * a certain zoom, so OL falls back to stretched tiles that misalign with the WMTS overlay).
   * Must be called whenever a basemap becomes active — both on manual switch (changeWorldLayer)
   * and on initial load, since the initial active basemap is set directly by the component
   * without going through changeWorldLayer.
   * @param basemap The newly active world layer.
   */
  activateWorldLayerZoomWatch(basemap: BaseLayer): void {
    // Remove any zoom listener set up for the previous basemap
    if (this._worldLayerZoomListener && this.openLayersMap) {
      this.openLayersMap.getView().un('change:resolution', this._worldLayerZoomListener);
      this._worldLayerZoomListener = null;
    }

    const googleType = basemap.get(MapService.TYPE_GOOGLE_LAYER_KEY);
    const maxZoom = basemap.getMaxZoom();

    if (googleType && isFinite(maxZoom) && this.openLayersMap) {
      const view = this.openLayersMap.getView();
      const listener = () => {
        this.googleBasemapVisible$.next((view.getZoom() ?? Infinity) <= maxZoom);
      };
      this._worldLayerZoomListener = listener;
      view.on('change:resolution', listener);
      listener(); // apply immediately at current zoom
    } else {
      this.googleBasemapVisible$.next(true);
    }
  }

  /**
   * Create a world layer from the JSON metadata sent as parameter.
   * It automatically adds it to the worldLayers dictionary.
   * If the layer already exists on the worldLayers dictionary, it returns that layer.
   * NB: THIS DO NOT ADD THE LAYER TO THE MAP, JUST CREATES IT.
   * @param layer JSON metadata of the world layer to create.
   * @returns A OpenLayers BaseLayer to be added to the map.
   */
  createWorldLayer(layer: LayerWorld): BaseLayer {
    if (!this.worldLayers.has(layer.id)) {
      let newLayer: TileLayer<any> = new TileLayer({
        visible: layer.ui_params.visible,
        minZoom: layer.ui_params.minZoom - 1 || -Infinity,
        maxZoom: layer.ui_params.maxZoom || Infinity,
        opacity: layer.ui_params.opacity
      });

      // Google Maps basemap type
      const googleMapsType: string = layer.ui_params.googleMapsTypology;

      if (googleMapsType === '' && layer.label == 'Open Street Map') {
        newLayer.setSource(new OSM());
      }
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.ORDER_WORLD_LAYER_KEY, layer.ui_params.order);
      newLayer.set(MapService.TYPE_GOOGLE_LAYER_KEY, layer.ui_params.googleMapsTypology);
      this.worldLayers.set(layer.id, newLayer);

      return newLayer;
    }

    return this.worldLayers.get(layer.id);
  }

  /**
   * Removes an OVERLAY layer if it already exists and is added to the activeLayers dictionary.
   * @param layerId The unique id of the layer.
   */
  removeLayerIfExisting(layerId: string): void {
    //If the layer is already added, delete it so it can be added again
    if (this.activeLayers.has(layerId)) {
      this.removeLayer(layerId);
      this.activeLayers.delete(layerId);
    }
  }

  /**
   * Checks if a layer group already exists, and if not, creates it and adds it to the layerGroup dictionary.
   * @param layerGroupName The unique layer group title.
   */
  checkAndCreateLayerGroup(layerGroupName: string): void {
    //Create the layer group if it does not exist and add it to the dictionary of layer groups.
    if (layerGroupName) {
      if (!this.layerGroups.has(layerGroupName)) {
        let newLayerGroup = new LayerGroup();
        this.layerGroups.set(layerGroupName, newLayerGroup);
      }
    }
  }

  /**
   * Create and add a WMS layer to the OpenLayers Map from a JSON metadata object.
   * If a layer with the same id exists, it deletes it and adds the new one.
   * @param layer The JSON metadata.
   * @param cache flag to check if the layer should involve caching its tiles or not
   * @returns A WMS BaseLayer if the operation was successful, or null if the layer was not added.
   */
  addWMSLayer(layer: LayerWMS, cache: boolean = false): BaseLayer | null {
    try {
      //If the layer is already added, delete it so it can be added again
      this.removeLayerIfExisting(layer.id);

      //Create the layer group if it does not exist
      this.checkAndCreateLayerGroup(layer.layer_group);

      // variable holding the layer object
      let newLayer: TileLayer<any>;
      // variable to keep track of this

      // extract the projection from the incoming data
      const projection = getProjection('EPSG:' + layer.params.srs);

      // if the layer requires authentication
      if (layer.auth) {
        // create a new tile layer with the data coming from the input layer object
        newLayer = new TileLayer({
          visible: layer.ui_params.visible,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          opacity: layer.ui_params.opacity,
          source: new TileWMS({
            // actual WMS layer as a source for the tile layer
            projection: cache ? projection : 'EPSG:3857', // reprojection doesn't work without caching
            url: this.proxyUrl + layer.url, // set the url as the proxy + the layer url
            crossOrigin: 'Anonymous',
            params: {
              // parameters for the tiles HTTP request
              SRS: layer.params.srs, // useful for WMS <1.3.0
              LAYERS: layer.params.layers, // name of the layer inside the server
              STYLES: layer.params.styles, // style to apply to the layer
              FORMAT: layer.params.format // format of the images coming from the server
            },
            cacheSize: cache ? 2048 : 0, // set the default caching size in case caching is enabled, otherwise set it to 0
            gutter: 20,
            transition: 0, // duration of the opacity effect during transition
            tileLoadFunction: this._authenticatedTileLoader(layer, true)
          })
        });
      } else {
        newLayer = new TileLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new TileWMS({
            projection: cache ? projection : 'EPSG:3857', // reprojection doesn't work without caching
            url: this.proxyUrl + layer.url,
            crossOrigin: 'Anonymous',
            params: {
              SRS: layer.params.srs,
              LAYERS: layer.params.layers,
              STYLES: layer.params.styles,
              FORMAT: layer.params.format
            },
            cacheSize: cache ? 2048 : 0,
            transition: 0 // Durata dell'effetto di opacità durante la transizione
          })
        });
      }

      //Set metadata as properties.
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.DATA_URL_LAYER_KEY, layer.data_url);
      newLayer.set(MapService.DATA_URL_DETAIL_LAYER_KEY, layer.detail_url);
      newLayer.set(MapService.GROUP_LAYER_KEY, layer.layer_group);
      newLayer.set(MapService.LAYER_OWNER_KEY, layer.owner);
      newLayer.set(MapService.LAYER_ACCESSIBILITY, layer.accessibility);

      this._setLayerOrder(newLayer, layer.layer_group);

      //Set the layer in the global dictionaries and the layer group.
      this.activeLayers.set(layer.id, newLayer);
      let currentLayersInGroup = this.layerGroups.get(layer.layer_group).getLayers();
      currentLayersInGroup.push(newLayer);
      this.layerGroups.get(layer.layer_group).setLayers(currentLayersInGroup);
      this.openLayersMap.addLayer(newLayer);
      return newLayer;
    } catch (error) {
      console.log(error);
      //If there was an error, return null
      return null;
    }
  }

  addImageWMSLayer(layer: LayerWMS): BaseLayer | null {
    try {
      //If the layer is already added, delete it so it can be added again
      this.removeLayerIfExisting(layer.id);

      //Create the layer group if it does not exist
      this.checkAndCreateLayerGroup(layer.layer_group);

      // variable holding the layer object
      let newLayer: ImageLayer<any>;
      // variable to keep track of this

      // extract the projection from the incoming data
      const projection = getProjection('EPSG:' + layer.params.srs);

      // if the layer requires authentication
      if (layer.auth) {
        // create a new tile layer with the data coming from the input layer object
        newLayer = new ImageLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new ImageWMS({
            // actual WMS layer as a source for the tile layer
            projection: projection, // reprojection doesn't work without caching
            url: this.proxyUrl + layer.url, // set the url as the proxy + the layer url
            crossOrigin: 'Anonymous',
            params: {
              // parameters for the tiles HTTP request
              SRS: layer.params.srs, // useful for WMS <1.3.0
              LAYERS: layer.params.layers, // name of the layer inside the server
              STYLES: layer.params.styles, // style to apply to the layer
              FORMAT: layer.params.format // format of the images coming from the server
            },
            ratio: 1,
            // cacheSize: cache ? 2048 : 0, // set the default caching size in case caching is enabled, otherwise set it to 0
            // gutter: 20,
            // transition: 0, // duration of the opacity effect during transition
            imageLoadFunction: this._authenticatedTileLoader(layer, true)
          })
        });
      } else {
        newLayer = new ImageLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new ImageWMS({
            projection: projection, // reprojection doesn't work without caching
            url: this.proxyUrl + layer.url,
            crossOrigin: 'Anonymous',
            params: {
              SRS: layer.params.srs,
              LAYERS: layer.params.layers,
              STYLES: layer.params.styles,
              FORMAT: layer.params.format
            },
            ratio: 1
          })
        });
      }

      //Set metadata as properties.
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.DATA_URL_LAYER_KEY, layer.data_url);
      newLayer.set(MapService.DATA_URL_DETAIL_LAYER_KEY, layer.detail_url);
      newLayer.set(MapService.GROUP_LAYER_KEY, layer.layer_group);
      newLayer.set(MapService.LAYER_OWNER_KEY, layer.owner);
      newLayer.set(MapService.LAYER_ACCESSIBILITY, layer.accessibility);

      this._setLayerOrder(newLayer, layer.layer_group);

      //Set the layer in the global dictionaries and the layer group.
      this.activeLayers.set(layer.id, newLayer);
      let currentLayersInGroup = this.layerGroups.get(layer.layer_group).getLayers();
      currentLayersInGroup.push(newLayer);
      this.layerGroups.get(layer.layer_group).setLayers(currentLayersInGroup);
      this.openLayersMap.addLayer(newLayer);
      return newLayer;
    } catch (error) {
      console.log(error);
      //If there was an error, return null
      return null;
    }
  }

  /**
   * Create and add a WMTS layer to the OpenLayers Map from a JSON metadata object.
   * If a layer with the same id exists, it deletes it and adds the new one.
   * @param {LayerWMTS} layer The JSON metadata.
   * @param {Object} capabilities capabilities obtained from GetCapabilities and parsed by OpenLayers
   * @returns {BaseLayer | null} A WMTS BaseLayer if the operation was successful, or null if the layer was not added.
   */
  addWMTSLayer(layer: LayerWMTS, maxZoom: number = 21, matrixLimit: boolean = false, capabilities = null): BaseLayer | null {
    try {
      //If the layer is already added, delete it so it can be added again
      this.removeLayerIfExisting(layer.id);

      //Create the layer group if it does not exist
      this.checkAndCreateLayerGroup(layer.layer_group);

      // object that will store the newly created WMTS layer
      let newLayer: TileLayer<any>;

      // keep track of this inside a callback function

      // if the function was given parsed capabilities
      if (capabilities) {
        // construct the options object with all the information from the capabilities
        // selected with the information from the layer
        let options = optionsFromCapabilities(capabilities, {
          layer: layer.params.layer,
          matrixSet: layer.params.tilematrixset,
          format: layer.params.format,
          crossOrigin: 'Anonymous',
          style: layer.params.styles
        });

        // replace the url to use the proxy url
        options.urls[0] = this.proxyUrl + layer.url;
        options.transition = 0; // duration of the opacity effect during transition

        // set the loading function to add the authorization token to every tile request in case of required auth
        if (layer.auth) {
          options.tileLoadFunction = this._authenticatedTileLoader(layer);
        }

        // create the new WMTS layer using the options to create the source
        newLayer = new TileLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new WMTS(options)
        });
      }
      // otherwise load the layer manually
      else {
        // calculate the matrices and resolutions required for the WMTS layer
        const projection = getProjection(layer.params.srs);
        const projectionExtent = projection.getExtent();
        const size = getWidth(projectionExtent) / 256; // EPSG:3857 = 256, EPSG:4326 = 512
        const resolutions = new Array(maxZoom);
        const matrixIds = new Array(maxZoom);

        for (let z = 0; z < maxZoom; ++z) {
          // generate resolutions and matrixIds arrays for this WMTS
          resolutions[z] = size / Math.pow(2, z);
          matrixIds[z] = matrixLimit ? layer.params.tilematrixset + ':' + z : z;
        }

        // create the WMTS layer manually with the info calculated and obtained from the layer object
        if (layer.auth) {
          newLayer = new TileLayer({
            visible: layer.ui_params.visible,
            opacity: layer.ui_params.opacity,
            minZoom: layer.ui_params.minZoom - 1 || -Infinity,
            maxZoom: layer.ui_params.maxZoom || Infinity,
            source: new WMTS({
              url: this.proxyUrl + layer.url,
              format: layer.params.format,
              layer: layer.params.layer,
              style: layer.params.styles,
              cacheSize: 2048,
              crossOrigin: 'Anonymous',
              transition: 0, // duration of the opacity effect during transition
              tileGrid: new WMTSTileGrid({
                origin: getTopLeft(projectionExtent),
                resolutions: resolutions,
                matrixIds: matrixIds
              }),
              matrixSet: layer.params.tilematrixset,
              projection: projection,
              tileLoadFunction: this._authenticatedTileLoader(layer)
            })
          });
        } else {
          newLayer = new TileLayer({
            visible: layer.ui_params.visible,
            opacity: layer.ui_params.opacity,
            minZoom: layer.ui_params.minZoom - 1 || -Infinity,
            maxZoom: layer.ui_params.maxZoom || Infinity,
            source: new WMTS({
              url: this.proxyUrl + layer.url,
              format: layer.params.format,
              layer: layer.params.layer,
              style: layer.params.styles,
              transition: 0, // duration of the opacity effect during transition
              tileGrid: new WMTSTileGrid({
                origin: getTopLeft(projectionExtent),
                resolutions: resolutions,
                matrixIds: matrixIds
              }),
              matrixSet: layer.params.tilematrixset,
              projection: projection
            })
          });
        }
      }

      //Set metadata as properties.
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.DATA_URL_LAYER_KEY, layer.data_url);
      newLayer.set(MapService.DATA_URL_DETAIL_LAYER_KEY, layer.detail_url);
      newLayer.set(MapService.GROUP_LAYER_KEY, layer.layer_group);
      newLayer.set(MapService.LAYER_OWNER_KEY, layer.owner);
      newLayer.set(MapService.LAYER_ACCESSIBILITY, layer.accessibility);
      // layer.minZoom && newLayer.set(MapService.MIN_ZOOM_LAYER_KEY, layer.minZoom);
      // layer.maxZoom && newLayer.set(MapService.MAX_ZOOM_LAYER_KEY, layer.maxZoom);

      this._setLayerOrder(newLayer, layer.layer_group);

      //Set the layer in the global dictionaries and the layer group.
      this.activeLayers.set(layer.id, newLayer);
      let currentLayersInGroup = this.layerGroups.get(layer.layer_group).getLayers();
      currentLayersInGroup.push(newLayer);
      this.layerGroups.get(layer.layer_group).setLayers(currentLayersInGroup);
      this.openLayersMap.addLayer(newLayer);
      return newLayer;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  /**
   * Create and add a XYZ layer to the OpenLayers Map from a JSON metadata object.
   * If a layer with the same id exists, it deletes it and adds the new one.
   * @param layer The JSON metadata.
   * @returns A XYZ BaseLayer if the operation was successful, or null if the layer was not added.
   */
  addXYZLayer(layer: LayerXYZ): BaseLayer | null {
    try {
      //If the layer is already added, delete it so it can be added again
      this.removeLayerIfExisting(layer.id);

      //Create the layer group if it does not exist
      this.checkAndCreateLayerGroup(layer.layer_group);

      let newLayer: TileLayer<any>;

      if (layer.auth) {
        newLayer = new TileLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new XYZ({
            url: this.proxyUrl + layer.url,
            transition: 0, // Durata dell'effetto di opacità durante la transizione
            tileLoadFunction: this._authenticatedTileLoader(layer)
          })
        });
      } else {
        newLayer = new TileLayer({
          visible: layer.ui_params.visible,
          opacity: layer.ui_params.opacity,
          minZoom: layer.ui_params.minZoom - 1 || -Infinity,
          maxZoom: layer.ui_params.maxZoom || Infinity,
          source: new XYZ({
            url: this.proxyUrl + layer.url,
            transition: 0 // Durata dell'effetto di opacità durante la transizione
          })
        });
      }

      //Set metadata as properties.
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.DATA_URL_LAYER_KEY, layer.data_url);
      newLayer.set(MapService.DATA_URL_DETAIL_LAYER_KEY, layer.detail_url);
      newLayer.set(MapService.GROUP_LAYER_KEY, layer.layer_group);
      newLayer.set(MapService.LAYER_OWNER_KEY, layer.owner);
      newLayer.set(MapService.LAYER_ACCESSIBILITY, layer.accessibility);

      this._setLayerOrder(newLayer, layer.layer_group);

      //Set the layer in the global dictionaries and the layer group.
      this.activeLayers.set(layer.id, newLayer);
      let currentLayersInGroup = this.layerGroups.get(layer.layer_group).getLayers();
      currentLayersInGroup.push(newLayer);
      this.layerGroups.get(layer.layer_group).setLayers(currentLayersInGroup);
      this.openLayersMap.addLayer(newLayer);
      return newLayer;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create and add a Vector layer to the OpenLayers Map from a JSON metadata object.
   * If a layer with the same id exists, it deletes it and adds the new one.
   * @param layer The JSON metadata.
   * @returns A XYZ BaseLayer if the operation was successful, or null if the layer was not added.
   */
  addVectorLayer(layer: LayerVector): BaseLayer | null {
    try {
      //If the layer is already added, delete it so it can be added again
      this.removeLayerIfExisting(layer.id);

      //Create the layer group if it does not exist
      this.checkAndCreateLayerGroup(layer.layer_group);

      let styles = undefined;

      if (layer.layerStyles && layer.layerStyles.length > 0) {
        const stylesKey = layer.layerStyles;
        styles = [];
        stylesKey.forEach((id) => {
          styles.push(this.layerStylesService.buildFeatureStyleFromId(id));
        });
      } else if (layer.customStyles && layer.customStyles.length > 0) {
        styles = (feature: Feature, resolution: number) => {
          const properties = feature.getProperties();
          const featureStyle = [];

          Object.keys(properties).forEach((propKey) => {
            // get the value
            let propValue = properties[propKey];

            // loop styles
            let confStyles = layer.customStyles.filter((style) => {
              let checkIfStylePresent = style.condition.find((cond) => {
                if (cond.field == propKey) {
                  // NEW for approvato = 0
                  if (!!cond.eq || cond.eq === 0) {
                    return propValue == cond.eq;
                  } else if (!!cond.gt || cond.gt === 0) {
                    return propValue >= cond.gt;
                  } else if (!!cond.lt || cond.lt === 0) {
                    return propValue <= cond.lt;
                  } // add others compare types
                }
                return false;
              });

              // if check passed
              if (!!checkIfStylePresent) {
                return true;
              }
              return false;
            });

            confStyles.forEach((el) => {
              el.style.forEach((styleKey) => {
                featureStyle.push(this.layerStylesService.buildFeatureStyleFromId(styleKey, feature.getProperties()));
              });
            });
            feature.setStyle(featureStyle);
          });
        };
      } else {
        const fill = new Fill({
          color: 'rgba(255,255,255,0.4)'
        });
        const stroke = new Stroke({
          color: '#3399CC',
          width: 1.25
        });

        styles = [
          new Style({
            image: new Circle({
              fill: fill,
              stroke: stroke,
              radius: 5
            }),
            fill: fill,
            stroke: stroke
          })
        ];
      }

      let format = layer.hasOwnProperty('format') ? new GeoJSON(layer['format']) : null;
      if (!format) {
        switch (layer.params.type) {
          case 'geojson':
            format = new GeoJSON({
              dataProjection: layer.params.dataProjection,
              featureProjection: layer.params.featureProjection ? layer.params.featureProjection : MapService.EPSG_3857_KEY
            });
            break;
          default:
            format = new GeoJSON({
              dataProjection: MapService.EPSG_3857_KEY,
              featureProjection: MapService.EPSG_3857_KEY
            });
            break;
        }
      }

      let strategy;
      if (layer.strategy && layer.strategy == 'bbox') {
        strategy = bbox;
      }

      let source;
      if (layer.auth) {
        source = new VectorSource({ strategy });
        source.setLoader((extent, _resolution, _projection) => {
          let src: string = layer.url;
          if (strategy) {
            src += `${src.indexOf('?') != -1 ? '&' : '?'}extent=${extent.join(',')}`;
          }

          let headers = new HttpHeaders();
          if (layer.auth.type === 'Basic') {
            headers = headers.set('Authorization', 'Basic ' + btoa(this._tokens[layer.auth.type][layer.id]));
          } else if (layer.auth.type === 'oauth') {
            headers = headers.set('Authorization', 'Bearer ' + this._tokens[layer.auth.type][layer.id]);
          }

          this._httpClient.request(layer.auth.method as any, src, { headers, responseType: 'json' as const }).subscribe({
            next: (data) => {
              if (data) {
                const features = format.readFeatures(data);
                features.forEach((feature) => {
                  source.addFeature(feature);
                });
              } else {
                console.error('Get request error');
              }
            },
            error: () => console.error('Get request error')
          });
        });
      } else {
        source = new VectorSource({
          url: layer.url, // this.proxyUrl + layer.url
          format: format
        });
      }

      const newLayer: VectorLayer<any> = new VectorLayer({
        visible: layer.ui_params.visible,
        opacity: layer.ui_params.opacity,
        minZoom: layer.ui_params.minZoom - 1 || -Infinity,
        maxZoom: layer.ui_params.maxZoom || Infinity,
        style: styles,
        source
      });

      //Set metadata as properties.
      newLayer.set(MapService.ID_LAYER_KEY, layer.id);
      newLayer.set(MapService.LABEL_LAYER_KEY, layer.label);
      newLayer.set(MapService.TYPE_LAYER_KEY, layer.type);
      newLayer.set(MapService.POSITION_LAYER_KEY, layer.position);
      newLayer.set(MapService.DATA_URL_LAYER_KEY, layer.data_url);
      newLayer.set(MapService.DATA_URL_DETAIL_LAYER_KEY, layer.detail_url);
      newLayer.set(MapService.GROUP_LAYER_KEY, layer.layer_group);
      newLayer.set(MapService.LAYER_OWNER_KEY, layer.owner);
      newLayer.set(MapService.LAYER_ACCESSIBILITY, layer.accessibility);

      this._setLayerOrder(layer, layer.layer_group);

      //Set the pop up HTML template
      if (layer.popupTemplate) {
        newLayer.set(MapService.POPUP_TEMPLATE_KEY, layer.popupTemplate);
      }

      //Set the layer in the global dictionaries and the layer group.
      this.activeLayers.set(layer.id, newLayer);
      let currentLayersInGroup = this.layerGroups.get(layer.layer_group).getLayers();
      currentLayersInGroup.push(newLayer);
      this.layerGroups.get(layer.layer_group).setLayers(currentLayersInGroup);
      this.openLayersMap.addLayer(newLayer);
      return newLayer;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  /** Centralized function to center the map. */
  setMapCenter(mapCenter: number[], epsg: string = MapService.EPSG_4326_KEY) {
    //Always in EPSG:4326

    let center3857;
    let center4326;

    if (epsg == MapService.EPSG_4326_KEY) {
      center3857 = transform(mapCenter, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
      center4326 = mapCenter;
    } else {
      center3857 = mapCenter;
      center4326 = transform(mapCenter, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    }

    this.mapCenter = center4326;

    if (this.readyOpenLayersMap) {
      this.openLayersMap.getView().setCenter(center3857);
      this.mapCenter = center4326;
      const mapConfiguration = this.mapConfigurationLoaded$.getValue();
      if (mapConfiguration && mapConfiguration.google_key && this.readyGoogleMapsMap) {
        this.googleMapsMap.googleMap.setCenter({ lat: center4326[1], lng: center4326[0] });
      }
    }
  }

  /**
   * Centralized point for setting a layer visibility. It uses the BaseLayer object from the activeLayers dictionary.
   * @param layerId The unique id of the layer.
   * @param visibility The new visibility value.
   */
  setLayerVisibility(layerId: string, visibility: boolean) {
    let layerObject = this.activeLayers.get(layerId);
    if (layerObject) {
      layerObject.setVisible(visibility);
    }

    // gestione della visibilità dei layer raggruppati
    this.activeLayers.forEach((lay, key) => {
      if (key && key.startsWith(`${layerId}_Group_`)) {
        if (lay) {
          lay.setVisible(visibility);
        }
      }
    });
  }

  /**
   * Sets every layer defined in the layer configurator (ebw-layers-configurator) as visible.
   * Mirrors the layer set and the setLayerVisibility call used by that component's confirm(),
   * so internal layers (marker/temp/base) not part of the configurator are left untouched.
   *
   * setLayerVisibility opera sugli oggetti OL già in activeLayers: questi vengono creati da
   * EbwMapComponent.setMapLayer(), che chiude con mapLayersLoaded$.next(true) solo DOPO aver atteso
   * addOverlayLayers() (incluse le fetch async delle capabilities WMTS di terze parti) — arrivare prima
   * di questo segnale, anche con mapConfigurationLoaded$ già popolato (la sola config JSON, non i layer
   * OL), fa sì che setLayerVisibility trovi activeLayers vuoto e non faccia nulla in silenzio. Su hard
   * reload di una rotta con sidebarAction già aperto (es. fattibilità) questa catena può non essere ancora
   * completa quando i chiamanti invocano showAllLayers() nel loro ngOnInit. mapLayersLoaded$ è una
   * BehaviorSubject (stesso pattern di dynamic-layers.component.ts): fa da fast-path se i layer sono già
   * pronti (caso navigazione SPA) e da trigger differito quando arrivano più tardi (caso hard reload).
   */
  showAllLayers() {
    this.mapLayersLoaded$
      .pipe(
        filter((loaded) => loaded === true),
        take(1)
      )
      .subscribe(() => {
        const config = this.mapConfigurationLoaded$.value?.sceltaRappresentazione;
        if (!config) return;
        // sceltaRappresentazione arriva dal backend in snake_case (IMapConfig), mentre LayerConfigurator è la
        // shape camelCase attesa da getConfigurationActiveLayers: nessun converter esiste tra le due, stesso
        // sidestep già usato altrove (es. ebw-layers-configurator.component.ts, map-wrapper.component.ts)
        this.getConfigurationActiveLayers(config as any).forEach((layer) => this.setLayerVisibility(layer.layerId, true));
      });
  }

  /**
   * Centralized point for setting a layer opacity. It uses the BaseLayer object from the activeLayers dictionary.
   * @param layerId The unique id of the layer.
   * @param opacity The new opacity value. Range [0-1]
   */
  setLayerOpacity(layerId: string, opacity: number) {
    let layerObject = this.activeLayers.get(layerId);
    if (layerObject) {
      layerObject.setOpacity(opacity);
    }
  }

  /**
   * Centralized point for getting a layer opacity. It uses the BaseLayer object from the activeLayers dictionary.
   * @param {string} layerId The unique id of the layer.
   * @returns {number} The opacity of the selected layer. Range [0-1]
   */
  getLayerOpacity(layerId: string): number {
    let layerObject = this.activeLayers.get(layerId);
    if (layerObject) {
      return layerObject.getOpacity();
    }

    return null;
  }

  /**
   * Get the list of the world (BASE) layers from the worldLayers dictionary.
   * @returns The list of world layers.
   */
  getWorldLayers(): { label: string; id: string; order: number }[] {
    let worldLayersIterator = this.worldLayers.values();
    return Array.from(worldLayersIterator).map((element) => {
      return {
        id: element.get(MapService.ID_LAYER_KEY),
        label: element.get(MapService.LABEL_LAYER_KEY),
        order: element.get(MapService.ORDER_WORLD_LAYER_KEY)
      };
    });
  }

  /**
   * Get the list of the overlay (OVERLAY) layers from the activeLayers dictionary.
   * @returns The list of active overlay layers.
   */
  getCustomLayers(gropuName = 'TYPE_CUSTOM'): { id: string; label: string; visible: boolean; owner: string; accessibility: any[] }[] {
    let groups = this.layerGroups.get(gropuName);
    let resp = [];
    if (groups) {
      resp = groups.getLayersArray().map((element) => {
        return {
          id: element.get(MapService.ID_LAYER_KEY),
          label: element.get(MapService.LABEL_LAYER_KEY),
          visible: element.get(MapService.VISIBILITY_LAYER_KEY),
          owner: element.get(MapService.LAYER_OWNER_KEY),
          accessibility: element.get(MapService.LAYER_ACCESSIBILITY)
        };
      });
    }
    return resp;
  }

  /**
   * Centralized place for removing an overlay layer from the map.
   * Uses the unique layer id.
   * @param layerId The unique layer id.
   */
  removeLayer(layerId: string) {
    let layerObject = this.activeLayers.get(layerId);
    if (layerObject) {
      //Delete the layer from the map and the active layers dictionary
      this.openLayersMap.removeLayer(layerObject);
      this.activeLayers.delete(layerId);

      //Delete the layer from the layer_group
      const layerGroupKey = layerObject.get(MapService.GROUP_LAYER_KEY);
      let layerGroupLayers = this.layerGroups.get(layerGroupKey).getLayers();
      layerGroupLayers.remove(layerObject);
      this.layerGroups.get(layerGroupKey).setLayers(layerGroupLayers);
    }
  }

  /**
   * Center the map given a coordinate in EPSG 4236 (for Google Maps) and an Extent (for OpenLayers).
   * @param coordinate Coordinate in EPSG 4236 to move the Google Maps to.
   * @param extent Extent in EPSG 3857 for fitting the open layers map.
   */
  centerMapOnCoordinatesAndExtent(coordinate: number[], extent: Extent) {
    //Coords 3857
    const coords3857 = coordinate;
    // const coords4326 = transform(coords3857, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    this.mapCenter = coords3857;
    // this.googleMapsMap.googleMap.setCenter({ lat: coords4326[1], lng: coords4326[0] });
    // prepare a default size in case the map dimensions are 0
    const size = this.openLayersMap.getSize();

    if (size[0] === 0) {
      size[0] = 500;
    }
    if (size[1] === 0) {
      size[1] = 500;
    }
    this.openLayersMap.getView().fit(extent, { size: size });
  }

  /**
   * Optional method to add and keep record of interactions to the map.
   * @param title The interaction unique identifier.
   * @param newInteraction The interaction object.
   */
  addMapInteraction(title: string, newInteraction: Interaction): void {
    if (!this.mapInteractions.get(title)) {
      this.mapInteractions.set(title, newInteraction);
      this.openLayersMap.addInteraction(newInteraction);
    }
  }

  /**
   * Optional method to remove interactions to the map that has been added with the addMapInteraction method.
   * @param title The interaction unique identifier.
   */
  removeMapInteraction(title: string): void {
    if (this.mapInteractions.get(title)) {
      let interactionToRemove = this.mapInteractions.get(title);
      this.openLayersMap.removeInteraction(interactionToRemove);
      this.mapInteractions.delete(title);
    }
  }

  /**
   * Adds a marker to the map with a title, location and an optional style.
   *
   * In practice, the marker is a Point feature specific style that is added to a layer called markerLayer.
   * @param title The unique title of the marker to be identified.
   * @param location The coordinates where the marker will be added.
   * @param markerStyle OPTIONAL style for the marker. If there is not markerStyle parameter, a default is used.
   */
  addMarker(title: string, location: Coordinate, markerStyle?: Style) {
    // Location must be in EPSG 4326
    let location3857 = transform(location, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);

    //Only add the marker layer if a marker is added.
    if (this.openLayersMap && !this.activeLayers.get(MapService.MARKER_LAYER_KEY)) {
      this.activeLayers.set(MapService.MARKER_LAYER_KEY, this.markerLayer);
      this.openLayersMap.addLayer(this.markerLayer);
    }

    let markerGeometry = new Point(location3857);

    let markerFeature = new Feature({
      title: title,
      geometry: markerGeometry
    });

    //If the optional style is present
    if (markerStyle) {
      markerFeature.setStyle(markerStyle);
    } else {
      //Put a generic style
      markerFeature.setStyle(
        new Style({
          image: new Icon({
            src: 'https://maps.gstatic.com/mapfiles/place_api/icons/v1/png_71/geocode-71.png',
            // src: 'assets/icons/position.png',
            crossOrigin: 'anonymous',
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        })
      );
    }

    this.markerLayer.getSource().addFeature(markerFeature);
    this.mapMarkers.set(title, markerFeature);
  }

  /**
   * Remove a marker that was added using the addMarker function.
   *
   * It removes it using the unique title of the marker.
   * @param title Unique title of the marker to delete.
   */
  removeMarker(title: string) {
    let openLayersMarker = this.mapMarkers.get(title);

    if (openLayersMarker) {
      this.mapMarkers.delete(title);
      this.markerLayer.getSource().removeFeature(openLayersMarker);
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------

  /**
   * Get the active layers according to the configuration file.
   *
   * Get the tab, title, layer object and visibility of the layers that are on the configuration file.
   * @param config The configuration of the layer selection.
   * @param mapZoom actual zoom level used on map
   * @returns List of ActiveLayers.
   */
  getConfigurationActiveLayers(config: LayerConfigurator[], mapZoom?: number): ActiveLayers[] {
    const layers: ActiveLayers[] = [];
    let showAlert = false;
    const iterableValuesLayers = this.activeLayers.values();
    Array.from(iterableValuesLayers).forEach((baseLayer: BaseLayer) => {
      config.forEach((tab) => {
        tab.content.forEach((group) => {
          group.values.forEach((layer: string | { children: any; name: string }, index) => {
            if (typeof layer === 'string') {
              // :small_blue_diamond: Caso semplice (vecchia struttura)
              if (layer === baseLayer.getProperties()[MapService.ID_LAYER_KEY]) {
                const layer = {
                  tabName: tab.tabName,
                  groupName: group.groupName,
                  layerId: baseLayer.get(MapService.ID_LAYER_KEY),
                  layerName: baseLayer.get(MapService.LABEL_LAYER_KEY),
                  visibility: baseLayer.get(MapService.VISIBILITY_LAYER_KEY),
                  disabled: baseLayer.get('disable')
                };
                if (mapZoom) {
                  const minZoom = baseLayer.getMinZoom() + 1;
                  const maxZoom = baseLayer.getMaxZoom();
                  // controllo se l layer può essere visibile a questo livello di zoom
                  const mustBeDisabled = mapZoom < minZoom || mapZoom > maxZoom;
                  // se almeno un layer acceso che era visibile non lo è più apro snackbar
                  if (mustBeDisabled && !layer.disabled && baseLayer.get(MapService.VISIBILITY_LAYER_KEY)) {
                    showAlert = true;
                  }
                  layer.disabled = mustBeDisabled;
                }
                layers.push(layer);
              }
            } else if (typeof layer === 'object' && layer.children) {
              layer.children.forEach((childLayer) => {
                if (childLayer === baseLayer.getProperties()[MapService.ID_LAYER_KEY]) {
                  const layer = {
                    tabName: tab.tabName,
                    groupName: group.groupName,
                    layerId: baseLayer.get(MapService.ID_LAYER_KEY),
                    layerName: baseLayer.get(MapService.LABEL_LAYER_KEY),
                    visibility: baseLayer.get(MapService.VISIBILITY_LAYER_KEY),
                    disabled: baseLayer.get('disable')
                  };
                  if (mapZoom) {
                    const minZoom = baseLayer.getMinZoom() + 1;
                    const maxZoom = baseLayer.getMaxZoom();
                    // controllo se l layer può essere visibile a questo livello di zoom
                    const mustBeDisabled = mapZoom < minZoom || mapZoom > maxZoom;
                    // se almeno un layer acceso che era visibile non lo è più apro snackbar
                    if (mustBeDisabled && !layer.disabled && baseLayer.get(MapService.VISIBILITY_LAYER_KEY)) {
                      showAlert = true;
                    }
                    layer.disabled = mustBeDisabled;
                  }
                  layers.push(layer);
                }
              });
            }
          });
        });
      });
    });
    if (showAlert) {
      const message = this._translateService.instant('map.sidebar.ebw_layers_configurator.layers_disabled');
      this.snackbarService.open(message, { duration: 3000 });
    }
    return layers;
  }

  // ------------------------------------------------------------------------------------------------------------------------------

  /**
   * Restore the default values of the service
   */
  resetService() {
    this.openLayersMap = null;
    this.googleMapsMap = null;
    this.googleMapsBasemapType = GoogleBasemapType.ROADMAP;
    this.mapCenter = null;
    this.mapZoom = null;
    this._mapExtent = null;
    this.showingMap = this.SHOW_OPEN_LAYERS;
    this.activeBaseLayer = null;

    this.worldLayers = new Map();
    this.activeLayers = new Map();
    this.layerGroups = new Map();
  }

  public emitReloadConfRequest() {
    this.mapConfigurationReloadRequest$.next(true);
  }

  public emitReloadAllConfRequest() {
    this.allConfigurationReloadRequest$.next(true);
  }

  /**
   * Center the map
   * @param geometry GeoJSON in 3857.
   */
  centerMapOnGeometry(geometry: any, geoJson: boolean = false, animate = false) {
    let geo = geometry;

    if (!geoJson) {
      geo = new GeoJSON().readGeometry(geometry);
    }

    if (animate) {
      this._animateflyTo(geo.getExtent());
    } else {
      // prepare a default size in case the map dimensions are 0
      const size = this.openLayersMap.getSize();

      if (size[0] === 0) {
        size[0] = 500;
      }
      if (size[1] === 0) {
        size[1] = 500;
      }
      this.openLayersMap.getView().fit(geo.getExtent(), { size: size });
    }
  }

  centerMapOnTempLayer(secondLayer = false) {
    try {
      const tempLayer = !secondLayer ? this.tempLayer.getSource() : this.tempLayer2.getSource();
      // se il layer non ha features l'extent è [Infinity, Infinity, -Infinity, -Infinity] e il centramento generara errore
      if (tempLayer.getFeatures().length == 0) {
        return;
      }

      // prepare a default size in case the map dimensions are 0
      const size = this.openLayersMap.getSize();

      if (size[0] === 0) {
        size[0] = 500;
      }
      if (size[1] === 0) {
        size[1] = 500;
      }
      this.openLayersMap.getView().fit(tempLayer.getExtent(), { size });
    } catch (error) {
      console.error('[mapService][centerMapOnTempLayer]', error);
    }
  }

  addFeatureToTemporaryLayer(
    geom: any,
    featureStyle: StyleLike,
    secondTempLayer = false,
    isGeomOLobject: boolean = false,
    id?: string,
    title?: string
  ) {
    let geometry;

    if (!isGeomOLobject) {
      const format = new GeoJSON({
        dataProjection: MapService.EPSG_3857_KEY,
        featureProjection: MapService.EPSG_3857_KEY
      });

      geometry = format.readGeometry(geom);
    } else {
      geometry = geom;
    }

    const feature = new Feature({ geometry: geometry });

    if (id) {
      feature.setId(id);
    }

    let style = featureStyle;

    if (!style) {
      style = this.layerStylesService.buildFeatureStyleFromId('SelectionStyle', feature.getProperties());
    }

    feature.setStyle(style);

    if (secondTempLayer) {
      this.tempLayer2.getSource().addFeature(feature);
    } else {
      this.tempLayer.getSource().addFeature(feature);
    }

    return feature;
  }

  clearFeatureFromTemporaryLayerById(id: string, secondTempLayer = false, deleteLike = false) {
    let layer = secondTempLayer ? this.tempLayer2 : this.tempLayer;
    let features = (layer.getSource() as VectorSource).getFeatures();
    let feature = layer.getSource().getFeatureById(id);

    if (deleteLike) {
      const featureToRemove = features.filter((feature) => (feature.getId() ? feature.getId().toString().includes(id) : true));
      if (featureToRemove) {
        layer.getSource().removeFeatures(featureToRemove);
      }
      return;
    }

    if (feature) {
      layer.getSource().removeFeature(feature);
    }
  }

  clearAllFeaturesFromTemporaryLayer(secondTempLayer = false) {
    let layer = secondTempLayer ? this.tempLayer2 : this.tempLayer;
    layer.getSource().clear();
  }

  addMarkerToTemporaryLayer(
    title: string,
    location: Coordinate,
    epsg: string = MapService.EPSG_4326_KEY,
    styleId = 'MarkerSelectionStyle',
    props = {}
  ): Feature<any> {
    //Location must be in EPSG 4326
    let location3857 = location;
    if (epsg == MapService.EPSG_4326_KEY) {
      location3857 = transform(location, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
    }

    const markerGeometry = new Point(location3857);

    const markerFeature = new Feature({
      title: title,
      geometry: markerGeometry,
      ...props
    });

    const markerStrokeStyle = this.layerStylesService.buildFeatureStyleFromId(styleId);

    markerFeature.setStyle(markerStrokeStyle);

    this.tempLayer.getSource().addFeature(markerFeature);
    this.tempMapMarkers.set(title, markerFeature);

    return markerFeature;
  }

  removeMarkerFromTemporaryLayer(title: string) {
    let openLayersMarker = this.tempMapMarkers.get(title);
    if (openLayersMarker) {
      this.tempLayer.getSource().removeFeature(openLayersMarker);
      this.tempMapMarkers.delete(title);
    }
  }

  removeAllMarkerFromTemporaryLayer() {
    const source = this.tempLayer.getSource();

    this.tempMapMarkers.forEach((feature) => {
      source.removeFeature(feature);
    });

    this.tempMapMarkers.clear();
  }

  // per il momento non serve ma tengo commenntato in caso servisse questa implementazione
  // generateImageWithNumber(txt: any) {
  //   // Creare un canvas
  //   const canvas = createCanvas(200, 200);
  //   const ctx = canvas.getContext('2d');

  //   ctx.fillStyle = '#FF00FF';
  //   ctx.beginPath();
  //   ctx.arc(canvas.width / 2, canvas.height / 2, 50, 0, 2 * Math.PI);
  //   ctx.fill();

  //   ctx.fillStyle = '#1C1B1F';
  //   ctx.font = '40px Arial';
  //   ctx.textAlign = 'center';
  //   ctx.textBaseline = 'middle';

  //   if (txt) {
  //     ctx.fillText(`${txt}`, canvas.width / 2, canvas.height / 2);
  //   }

  //   return canvas.toDataURL('image/png');
  // }

  transformGeometry(coordinates: any[], mode: '4326to3857' | '3857to4326' = '4326to3857') {
    const { EPSG_4326_KEY, EPSG_3857_KEY } = MapService;
    let sourceProjection = '';
    let destinationProjection = '';

    switch (mode) {
      case '4326to3857':
        sourceProjection = EPSG_4326_KEY;
        destinationProjection = EPSG_3857_KEY;
        break;
      case '3857to4326':
        sourceProjection = EPSG_3857_KEY;
        destinationProjection = EPSG_4326_KEY;
        break;

      default:
        break;
    }
    // non ho coordinate
    if (!coordinates?.length) {
      return [];
    }
    // ho un punto
    if (!Array.isArray(coordinates[0])) {
      const coords = transform(coordinates, sourceProjection, destinationProjection);
      return coords;
    }
    // ho una linea
    if (!Array.isArray(coordinates[0][0])) {
      const coords = coordinates.map((coord: number[]) => transform(coord, sourceProjection, destinationProjection));
      return coords;
    }
    // ho una multilinea
    const coords = coordinates.map((coordArray: number[][]) =>
      coordArray.map((coord: number[]) => transform(coord, sourceProjection, destinationProjection))
    );
    return coords;
  }

  transformGeometryCordsFrom4326To3857(coordinates) {
    // // non ho coordinate
    // if (!coordinates?.length) {
    //   return [];
    // }
    // const { EPSG_4326_KEY, EPSG_3857_KEY } = MapService;
    // // ho un punto
    // if (!Array.isArray(coordinates[0])) {
    //   const coords = transform(coordinates, EPSG_4326_KEY, EPSG_3857_KEY);
    //   return coords;
    // }
    // // ho una linea
    // if (!Array.isArray(coordinates[0][0])) {
    //   const coords = coordinates.map((coord: number[]) => transform(coord, EPSG_4326_KEY, EPSG_3857_KEY));
    //   return coords;
    // }
    // // ho una multilinea
    // const coords = coordinates.map((coordArray: number[][]) =>
    //   coordArray.map((coord: number[]) => transform(coord, EPSG_4326_KEY, EPSG_3857_KEY))
    // );
    // return coords;
    const { EPSG_4326_KEY, EPSG_3857_KEY } = MapService;

    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return [];
    }

    try {
      // Point: [lon, lat]
      if (!Array.isArray(coordinates[0])) {
        return transform(coordinates, EPSG_4326_KEY, EPSG_3857_KEY);
      }

      // LineString: [[lon, lat], ...]
      if (!Array.isArray(coordinates[0][0])) {
        return coordinates.map((coord: number[]) => transform(coord, EPSG_4326_KEY, EPSG_3857_KEY));
      }

      // Polygon: [[[lon, lat], ...], ...]
      if (!Array.isArray(coordinates[0][0][0])) {
        return coordinates.map((ring: number[][]) => ring.map((coord: number[]) => transform(coord, EPSG_4326_KEY, EPSG_3857_KEY)));
      }

      // MultiPolygon or MultiLineString: [[[[lon, lat], ...], ...], ...]
      return coordinates.map((part: number[][][]) =>
        part.map((ring: number[][]) => ring.map((coord: number[]) => transform(coord, EPSG_4326_KEY, EPSG_3857_KEY)))
      );
    } catch (error) {
      console.error('Errore nella trasformazione delle coordinate:', error);
      return [];
    }
  }

  // TODO #MODIFICHE-MAIN (questo dove è usato)
  showTimeline(conf: { name: string; id: any; historyField: string; visibility: boolean }) {
    this.timelines$.next(conf);
  }

  /**
   * Method for applying CQL filters to a WMS layer.
   *
   * @param {string} layerTitle Name of the layer to be filtered.
   * @param {string} date Date to be used in the filter.
   * @param {string} field Field to be used in the filter.
   */
  public setLayerCQL(layerTitle: string, date: string, field: string): void {
    this.clearCache(layerTitle, true);
    // get the layer object
    const layerObject: Layer = this.activeLayers.get(layerTitle) as Layer;

    // if no layer object was found, return
    if (!layerObject) {
      return;
    }

    // extract the source from the layer object
    const layerSource: Source = layerObject.getSource();

    // if the source if a TileWMS or ImageWMS, update the parameters with the CQL filter
    if (layerSource instanceof TileWMS || layerSource instanceof ImageWMS) {
      layerSource.updateParams({ CQL_FILTER: `${field} = ${date}` });
    }
  }

  /**
   * clear the cache of all the layers with a TileCache or the one specified
   * @param {string} layerName optional parameter to specify a single layer to be cleared
   * @param {boolean} exceptCurrentZ optional parameter to specify if the cache should be cleared or everything but the current Z level. Defaults to false.
   */
  clearCache(layerName: string = undefined, exceptCurrentZ: boolean = false): void {
    // get the list of layers available in the map
    const layers = this.openLayersMap.getLayers().getArray();

    // if we're clearing a single layer
    if (layerName) {
      // find the layer in the list
      const layerObject = layers.find((el) => {
        return el.getProperties().title === layerName;
      });

      // if the layer was found
      if (layerObject) {
        const layerProperties = layerObject.getProperties();
        // if the selected layer has a source
        if (layerProperties.source) {
          // if the selected layer's source has a tile cache
          if (layerProperties.source.tileCache) {
            // clear the tilecache
            if (exceptCurrentZ) {
              layerProperties.source.tileCache.pruneExceptNewestZ();
            } else {
              layerProperties.source.tileCache.clear();
            }
          }
          // refresh the source of the layer
          if (!exceptCurrentZ) {
            layerProperties.source.refresh();
          }
        }
      }

      return;
    }

    // if there was no layer selected, cycle through all the layers in the map
    for (const layer of layers) {
      const layerProperties = layer.getProperties();
      // if the layer has a source
      if (layerProperties.source) {
        // if the source has a tile cache
        if (layerProperties.source.tileCache) {
          // clear the tilecache
          if (exceptCurrentZ) {
            layerProperties.source.tileCache.pruneExceptNewestZ();
          } else {
            layerProperties.source.tileCache.clear();
          }
        }
        // refresh the source of the layer
        if (!exceptCurrentZ) {
          layerProperties.source.refresh();
        }
      }
    }
  }

  /**
   * function to set the z-index of the layer depending on the layerGroup
   * @param layer OpenLayers layer object
   * @param layerGroup string containing the name of the layer group
   */
  private _setLayerOrder(layer, layerGroup) {
    let zIndex = this.layerOrder.indexOf(layerGroup);

    if (!zIndex || zIndex == -1) {
      return;
    }

    layer.setZIndex(zIndex);
  }

  private _animateflyTo(extent) {
    //https://openlayers.org/en/latest/examples/animation.html
    const view = this.openLayersMap.getView();
    const duration = 2500;
    const zoom = 17;

    view.animate({
      center: extent,
      duration: duration
    });

    view.animate(
      {
        zoom: zoom - 1,
        duration: duration / 2
      },
      {
        zoom: zoom,
        duration: duration / 2
      }
    );
  }

  // -----------------------------------------------------------------------

  clearLayerCache() {
    // clear cache dei layer geoserver
    for (const [_, layer] of this.activeLayers.entries()) {
      if (layer.getVisible()) {
        // TODO migliorare facendolo per layer specifici
        this.clearCache(); // questo pulisce la cache a fondo

        // N.B. questo sotto non funziona, lo lascio per ricordarcelo
        // (layer as TileLayer<any>).getSource().clear();
        // this.mapService.openLayersMap.renderSync();
      }
    }
  }
}
